// ============================================================================
// STORE.TRIAGE.JS — Triage Decision + Status Transition Layer (Stage 2/3)
//
// Functions:
//   setTriageDecision(tenantId, referralId, triageDecision, uid, role)
//   approveToScheduling(tenantId, referralId, uid, role)
//   sendToNeedsInfo(tenantId, referralId, uid, role)
//   returnToIntakeForRework(tenantId, referralId, reason, selectedItemTypes, uid)
//
// Design:
//   • setTriageDecision: writeBatch — updates triage.* in workflow/state AND
//     creates a TRIAGE_UPDATED workflowEvents doc atomically. Fills the gap
//     in the original store.workflow.js setTriageDecision which did not write
//     TRIAGE_UPDATED events.
//   • approveToScheduling / sendToNeedsInfo: thin wrappers over applyStatusTransition
//     (canonical FSM enforcement). approveToScheduling guards missingItemsCount > 0.
//
// This module is NOT re-exported from core/index.js to avoid a naming conflict
// with setTriageDecision in store.workflow.js (used by WorkItemDrawer).
// TriageView.jsx imports directly: import { ... } from '../core/store.triage.js'
//
// Zero-PHI invariant:
//   No patient name, DOB, or PHI stored. Only status, triage enums, timestamps,
//   actorUid, and actorRole are written.
//
// Firestore paths:
//   tenants/{tenantId}/referrals/{referralId}/workflow/state   (update)
//   tenants/{tenantId}/referrals/{referralId}/workflowEvents/{eventId} (create)
// ============================================================================

import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  getDoc,
  writeBatch,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';
import {
  applyStatusTransition,
  WORKFLOW_STATUS,
  WORKFLOW_EVENT_TYPES,
  WORKFLOW_PRIORITY,
  TRIAGE_DECISION,
} from './store.workflow.js';
import { buildReworkRequestDoc, REWORK_REQUEST_STATUS } from './store.rework.js';

// ── DB singleton ──────────────────────────────────────────────────────────────

function getDb() {
  return getFirestore(getApp());
}

// ── ID generator ─────────────────────────────────────────────────────────────

function generateEventId() {
  return `wfe_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── setTriageDecision ─────────────────────────────────────────────────────────

/**
 * Atomically set a triage decision and write a TRIAGE_UPDATED workflow event.
 *
 * Does NOT change workflow status. Status transitions (→NEEDS_INFO,
 * →READY_TO_SCHEDULE) are separate actions via sendToNeedsInfo /
 * approveToScheduling.
 *
 * Invariant: every call to this function produces exactly one TRIAGE_UPDATED
 * workflowEvents document, satisfying the event-log requirement.
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @param {string} triageDecision — one of TRIAGE_DECISION enum values
 * @param {string} uid — acting user's uid
 * @param {string} role — acting user's role (written to event actorRole)
 */
export async function setTriageDecision(tenantId, referralId, triageDecision, uid, role) {
  const db  = getDb();
  const now = serverTimestamp();

  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, generateEventId()));

  // Read fresh state to capture current status for the event record.
  const snap       = await getDoc(stateRef);
  const fromStatus = snap.exists() ? snap.data().status : null;

  const batch = writeBatch(db);

  // Update triage sub-object in workflow/state
  batch.update(stateRef, {
    'triage.triageDecision':  triageDecision,
    'triage.triageUpdatedAt': now,
    'triage.triageByUid':     uid,
    updatedAt:                now,
    updatedByUid:             uid,
  });

  // Append-only TRIAGE_UPDATED event
  batch.set(eventRef, {
    tenantId,
    referralId,
    eventType:      WORKFLOW_EVENT_TYPES.TRIAGE_UPDATED,
    fromStatus,
    toStatus:       fromStatus, // status unchanged by this action
    triageDecision,
    actorUid:       uid,
    actorRole:      role,
    createdAt:      now,
  });

  await batch.commit();
}

// ── approveToScheduling ───────────────────────────────────────────────────────

/**
 * Approve a referral for scheduling (NEW|NEEDS_INFO → READY_TO_SCHEDULE).
 *
 * Guards: missingItemsCount must be 0. If rework items remain the store
 * throws so the UI can show a clear error (button should already be disabled
 * by the UI for this case — this is a defense-in-depth check).
 *
 * Delegates to applyStatusTransition for FSM validation, role check, and
 * atomic STATUS_CHANGED event write.
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @param {string} uid
 * @param {string} role
 * @throws {Error} if missingItemsCount > 0
 * @throws {Error} if FSM or role validation fails (from applyStatusTransition)
 */
export async function approveToScheduling(tenantId, referralId, uid, role) {
  const db       = getDb();
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const snap     = await getDoc(stateRef);

  if (!snap.exists()) {
    throw new Error('Workflow state not found.');
  }

  const { rework } = snap.data();
  const missingCount = rework?.missingItemsCount ?? 0;

  if (missingCount > 0) {
    throw new Error(
      `Cannot approve to scheduling: ${missingCount} missing item${missingCount !== 1 ? 's' : ''} remain. Resolve rework first.`
    );
  }

  return applyStatusTransition({
    tenantId,
    referralId,
    toStatus:  WORKFLOW_STATUS.READY_TO_SCHEDULE,
    actorUid:  uid,
    actorRole: role,
  });
}

// ── sendToNeedsInfo ───────────────────────────────────────────────────────────

/**
 * Send a referral to the NEEDS_INFO queue (NEW → NEEDS_INFO).
 *
 * Delegates to applyStatusTransition for FSM validation, role check, and
 * atomic STATUS_CHANGED event write.
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @param {string} uid
 * @param {string} role
 * @throws {Error} if FSM or role validation fails (from applyStatusTransition)
 */
export async function sendToNeedsInfo(tenantId, referralId, uid, role) {
  return applyStatusTransition({
    tenantId,
    referralId,
    toStatus:  WORKFLOW_STATUS.NEEDS_INFO,
    actorUid:  uid,
    actorRole: role,
  });
}

// ── saveTriage ────────────────────────────────────────────────────────────────

/**
 * Atomically save bounded triage fields to workflow/state and append a
 * TRIAGE_UPDATED workflowEvent. Does NOT change workflow status.
 *
 * Bounded fields (only these may be written by triage_nurse):
 *   priority, onHold, onHoldReason, overrideDecision, overrideReason,
 *   overrideNote, triageNote, triageCompletedBy (when markComplete=true),
 *   triageCompletedAt (when markComplete=true).
 *
 * triageAction is derived from what is being set:
 *   markComplete=true  → TRIAGE_COMPLETED
 *   onHold=true        → TRIAGE_ON_HOLD
 *   overrideDecision   → TRIAGE_OVERRIDE_APPLIED
 *   else               → TRIAGE_SAVED
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @param {object} fields — bounded triage fields (any subset)
 * @param {string} uid — acting user uid
 * @param {boolean} [markComplete=false] — set triageCompletedBy/At
 * @returns {Promise<void>}
 * @throws {Error} if workflow/state does not exist
 */
export async function saveTriage(tenantId, referralId, fields, uid, markComplete = false) {
  const db  = getDb();
  const now = serverTimestamp();

  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, generateEventId()));

  const snap = await getDoc(stateRef);
  if (!snap.exists()) {
    throw new Error(`Workflow state not found for referral ${referralId}.`);
  }
  const currentData = snap.data();
  const fromStatus  = currentData.status;

  // Derive triageAction and eventType from what is being set
  let triageAction = 'TRIAGE_SAVED';
  let eventType = WORKFLOW_EVENT_TYPES.TRIAGE_UPDATED;
  if (markComplete) {
    triageAction = 'TRIAGE_COMPLETED';
    eventType = WORKFLOW_EVENT_TYPES.TRIAGE_APPROVED;
  } else if (fields.onHold === true) {
    triageAction = 'TRIAGE_ON_HOLD';
    eventType = WORKFLOW_EVENT_TYPES.TRIAGE_ON_HOLD;
  } else if (fields.onHold === false && currentData.onHold === true) {
    triageAction = 'TRIAGE_HOLD_RELEASED';
    eventType = WORKFLOW_EVENT_TYPES.TRIAGE_HOLD_RELEASED;
  } else if (fields.overrideDecision) {
    triageAction = 'TRIAGE_OVERRIDE_APPLIED';
  }

  // Build state update — only bounded triage fields
  const stateUpdate = { updatedAt: now, updatedByUid: uid };
  if (fields.priority         !== undefined) stateUpdate.priority         = fields.priority;
  if (fields.onHold           !== undefined) {
    stateUpdate.onHold = fields.onHold;
    if (fields.onHold === true) {
      stateUpdate.onHoldStartedAt = now;
      stateUpdate.onHoldBy        = uid;
    } else {
      stateUpdate.onHoldReleasedAt = now;
      stateUpdate.onHoldReleasedBy = uid;
    }
  }
  if (fields.onHoldReason     !== undefined) stateUpdate.onHoldReason     = fields.onHoldReason;
  if (fields.overrideDecision !== undefined) stateUpdate.overrideDecision = fields.overrideDecision;
  if (fields.overrideReason   !== undefined) stateUpdate.overrideReason   = fields.overrideReason;
  if (fields.overrideNote     !== undefined) stateUpdate.overrideNote     = fields.overrideNote;
  if (fields.triageNote       !== undefined) stateUpdate.triageNote       = fields.triageNote;
  // Stamp overriddenBy / overriddenAt when an override decision is being applied.
  // These are bounded triage fields and the canonical source for override visibility.
  if (fields.overrideDecision) {
    stateUpdate.overriddenBy = uid;
    stateUpdate.overriddenAt = now;
  }
  // Always stamp triage subobject so triageByUid / triageUpdatedAt reflect the
  // last nurse to act, and triageDecision is set to OK on completion.
  stateUpdate['triage.triageByUid']      = uid;
  stateUpdate['triage.triageUpdatedAt']  = now;
  if (markComplete) {
    stateUpdate['triage.triageDecision']   = TRIAGE_DECISION.OK;
    stateUpdate['triage.triageCompletedBy'] = uid;
    stateUpdate['triage.triageCompletedAt'] = now;
    stateUpdate.isTriaged        = true;
    stateUpdate.triageCompletedAt = now;
    stateUpdate.triageCompletedBy = uid;
  }

  // ── Flow Quality — Triage Exception (idempotent) ────────────────────────────
  // Detect exception type for this save. Priority escalation = moving ABOVE NORMAL
  // (HIGH or URGENT). Check incoming priority against current stored priority.
  let exceptionType = null;
  if (fields.overrideDecision) {
    exceptionType = 'OVERRIDE';
  } else if (fields.onHold === true) {
    exceptionType = 'ON_HOLD';
  } else if (
    fields.priority !== undefined &&
    fields.priority !== WORKFLOW_PRIORITY.NORMAL &&
    currentData.priority !== fields.priority
  ) {
    exceptionType = 'PRIORITY_ESCALATION';
  }

  // Only write the first exception ever encountered for this referral.
  const isFirstException = exceptionType !== null && !currentData.triageExceptionAt;
  if (isFirstException) {
    stateUpdate.triageExceptionAt   = now;
    stateUpdate.triageExceptionType = exceptionType;
  }

  const batch = writeBatch(db);

  batch.update(stateRef, stateUpdate);

  batch.set(eventRef, {
    tenantId,
    referralId,
    eventType,
    triageAction,
    fromStatus,
    toStatus:      fromStatus,
    actorUid:      uid,
    actorRole:     'triage_nurse',
    overrideReason: fields.overrideReason ?? null,
    onHoldReason:  fields.onHoldReason   ?? null,
    priority:      fields.priority        ?? null,
    notePreview:   fields.triageNote
      ? String(fields.triageNote).slice(0, 120)
      : null,
    createdAt: now,
  });

  // If this is the first triage exception, append a second event in the same batch.
  if (isFirstException) {
    const exceptionEventId  = generateEventId();
    const exceptionEventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, exceptionEventId));
    batch.set(exceptionEventRef, {
      tenantId,
      referralId,
      eventType:          WORKFLOW_EVENT_TYPES.TRIAGE_EXCEPTION_RECORDED,
      triageExceptionType: exceptionType,
      fromStatus,
      toStatus:           fromStatus,
      actorUid:           uid,
      actorRole:          'triage_nurse',
      createdAt:          now,
    });
  }

  await batch.commit();
}

// ── returnToIntakeForRework ──────────────────────────────────────────────────

/**
 * Return a referral from triage back to intake for rework.
 *
 * Atomic batch:
 *   1. workflow/state  — status = NEEDS_INFO, statusEnteredAt, returnedToIntake metadata
 *   2. workflowEvent   — RETURNED_TO_INTAKE with reason + item types
 *   3. reworkRequest(s) — one flat doc per selected item type (source = TRIAGE_RETURN)
 *
 * Guards:
 *   - Current status must be READY_TO_SCHEDULE (FSM enforced).
 *   - reason is required (non-empty string).
 *   - At least one item type must be selected.
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {string}   reason            — triage nurse's written reason for return
 * @param {string[]} selectedItemTypes — one or more REWORK_ITEM_TYPE values
 * @param {string}   uid               — acting triage_nurse uid
 */
export async function returnToIntakeForRework(tenantId, referralId, reason, selectedItemTypes, uid) {
  if (!reason || !reason.trim()) {
    throw new Error('A reason is required when returning a referral to intake.');
  }
  if (!selectedItemTypes || selectedItemTypes.length === 0) {
    throw new Error('Select at least one missing/invalid item type.');
  }

  const db  = getDb();
  const now = serverTimestamp();

  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const snap     = await getDoc(stateRef);
  if (!snap.exists()) {
    throw new Error('Workflow state not found. Cannot return to intake.');
  }

  const current    = snap.data();
  const fromStatus = current.status;

  if (fromStatus !== WORKFLOW_STATUS.READY_TO_SCHEDULE) {
    throw new Error(
      `Cannot return to intake: current status is "${fromStatus}", expected "${WORKFLOW_STATUS.READY_TO_SCHEDULE}".`
    );
  }

  const batch      = writeBatch(db);
  const eventId    = generateEventId();
  const referralRef = doc(db, PATHS.referral(tenantId, referralId));

  // 0. Denormalize workflowStatus + the GP-inbox discriminator onto the root referral
  //    doc so inbox queries find it. awaitingGpDocs = true because this triage return
  //    creates >=1 OPEN document request the GP must upload (cleared by the
  //    attachment-ready Cloud Function when the last request is received).
  //    affectedKeys() allowlist in firestore.rules permits exactly these three keys.
  batch.update(referralRef, {
    workflowStatus:          WORKFLOW_STATUS.NEEDS_INFO,
    workflowStatusEnteredAt: now,
    awaitingGpDocs:          true,
  });

  // 1. Update workflow/state → NEEDS_INFO
  batch.update(stateRef, {
    status:                  WORKFLOW_STATUS.NEEDS_INFO,
    statusEnteredAt:         now,
    'rework.missingItemsCount': increment(selectedItemTypes.length),
    'rework.returnedToIntakeAt':    now,
    'rework.returnedToIntakeByUid': uid,
    'rework.returnedToIntakeReason': reason.trim(),
    updatedAt:    now,
    updatedByUid: uid,
  });

  // 2. Append RETURNED_TO_INTAKE workflowEvent
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  batch.set(eventRef, {
    tenantId,
    referralId,
    eventType:  WORKFLOW_EVENT_TYPES.RETURNED_TO_INTAKE,
    fromStatus,
    toStatus:   WORKFLOW_STATUS.NEEDS_INFO,
    reason:     reason.trim(),
    itemTypes:  selectedItemTypes,
    source:     'TRIAGE_RETURN',
    actorUid:   uid,
    actorRole:  'triage_nurse',
    createdAt:  now,
  });

  // 3. Create one reworkRequest doc per selected item type
  for (const itemType of selectedItemTypes) {
    const requestId  = generateEventId().replace('wfe_', 'req_');
    const requestRef = doc(db, PATHS.reworkRequest(tenantId, referralId, requestId));
    const requestDoc = buildReworkRequestDoc({
      tenantId,
      referralId,
      requestId,
      itemType,
      createdByUid:  uid,
      createdByRole: 'triage_nurse',
    });
    batch.set(requestRef, {
      ...requestDoc,
      source:    'TRIAGE_RETURN',
      createdAt: now,
      updatedAt: now,
    });
  }

  await batch.commit();
}
